/**
 * WS-O review-1 B-1: the RUN's refusals, as one badge a panel can render.
 *
 * The wave deleted the only panel sentence that said "K files could not be written — <reason>"
 * and replaced it with nothing, so a disk-full run finished "1000/1000" in silence. This is that
 * sentence, sourced from the run writer's own report and worded by the SAME
 * `exportProblemSummary` the copy verbs use — two surfaces wording one outcome differently is
 * how an operator learns not to trust either.
 *
 * It lives in its own module rather than beside `exportProblemSummary` because
 * `export/bundle.ts` is a pinned zero-diff file for this wave (its rules and its two Error-log
 * sentences are exactly what WS-O promised not to touch).
 *
 * review-1 m-4, recorded here because it is about the same surface: `ExportReport.openError`
 * and `openFailureSuffix` in `bundle.ts` have NO production reader on purpose. WS-O moved the
 * reveal out of `exportBundle` and into the run writer (one reveal per run, fired from
 * `RunWriter.finish()` when the run ENDS — the owner's "it should open when the mockups are
 * generated"), and a failed reveal is surfaced where the operator can act on it — the
 * panels' `batch-open-error` / `transparent-open-error` rows, which carry the message AND a
 * Retry. Both are kept for compile compatibility with callers that spread a report, and because
 * `runWriter` still records the value for a future surface.
 */
import { exportProblemSummary, type ExportReport } from "./bundle.js";

export function runProblemNotice(
  report: ExportReport | undefined,
): { text: string; cls: string } | null {
  if (!report) return null;
  const parts: string[] = [];
  const summary = exportProblemSummary(report).trim();
  if (summary) parts.push(summary);
  if (report.missing.length > 0) {
    // `missing` rides along because `exportProblemSummary` (which the copy verbs share) does not
    // carry it, and a byte-less item is still a file that is not on disk.
    parts.push(
      `${report.missing.length} file${report.missing.length === 1 ? "" : "s"} had nothing to write — ` +
        `${report.missing[0].ref}: ${report.missing[0].reason}` +
        (report.missing.length > 1
          ? ` (+${report.missing.length - 1} more in Settings → Observability → Error log)`
          : ""),
    );
  }
  // `null` when there is nothing to say: a badge that is always there is a badge nobody reads.
  if (parts.length === 0) return null;
  // WS-B's status vocabulary: `badge--danger` only for a genuine loss. A refused WRITE is one;
  // a collision or a byte-less item is a file the operator can still recover by renaming or
  // re-rendering, so it stays a warn.
  // review-2 F-1: `badge--wrap` on every one of these. This is the only badge in the app whose
  // text is a SENTENCE, and `.badge`'s `white-space: nowrap` inside a `contain: layout paint`
  // card clipped the platform's own USB/permission refusal by 389px at 1280 (645px at 1024) —
  // silently, with no ellipsis and no `title`, and what it cut was the recovery half. The panels
  // add `title` on top so the full text is reachable even if a future card narrows further.
  return {
    text: parts.join(" "),
    cls: report.failed.length > 0
      ? "badge badge--wrap badge--danger"
      : "badge badge--wrap badge--warn",
  };
}
